import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import styled, { css } from 'styled-components';

import { PlainButton } from '../../components/pageStyles';
import { tint, wz, wzType } from '../../wizard/wizardStyles';

type TileSize = 'outcome' | 'reward';

// 6985:5608 `obtn` (74 tall, r12, a 38 chip at r10, Bold 15) and 6985:11533 `rtile` (78 tall, r13,
// a 40 chip at r11, Bold 14.5): an icon chip 21 / 20 in, then the label.
const SIZES = {
  outcome: { height: 74, radius: 12, chip: 38, chipRadius: 10, padding: 21, type: wzType.question },
  reward: { height: 78, radius: 13, chip: 40, chipRadius: 11, padding: 20, type: wzType.body }
} as const;

const Tile = styled(PlainButton)<{ $selected: boolean; $size: TileSize }>`
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: ${({ $size }) => SIZES[$size].height}px;
  padding: 0 ${({ $size }) => SIZES[$size].padding}px;
  border-radius: ${({ $size }) => SIZES[$size].radius}px;
  color: ${wz.text};
  font-weight: 700;
  text-align: left;
  ${({ $size }) => SIZES[$size].type}

  ${({ $selected }) =>
    $selected
      ? css`
          border: 1px solid ${wz.accent};
          background: ${tint(wz.accent, 0.14)};
        `
      : css`
          border: 1px solid ${wz.border};
          background: linear-gradient(180deg, ${wz.field} 0%, ${wz.fieldEnd} 100%);
        `}

  > * + * {
    margin-left: 14px;
  }

  &:focus-visible {
    outline: 2px solid ${wz.accent};
    outline-offset: 2px;
  }
`;

const Chip = styled.span<{ $selected: boolean; $size: TileSize }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => SIZES[$size].chip}px;
  height: ${({ $size }) => SIZES[$size].chip}px;
  border-radius: ${({ $size }) => SIZES[$size].chipRadius}px;
  background: ${({ $selected }) => ($selected ? wz.accent : tint(wz.accent, 0.14))};
  color: ${({ $selected }) => ($selected ? wz.page : wz.accent)};
  font-size: 18px;
`;

interface IOptionTileProps {
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  onSelect: () => void;
  size?: TileSize;
  testId?: string;
}

/** A row tile: the rule outcome picker (45) and the reward type picker (56). */
export const OptionTile: React.FC<IOptionTileProps> = ({
  icon,
  label,
  selected,
  onSelect,
  size = 'outcome',
  testId
}) => (
  <Tile
    type="button"
    aria-pressed={selected}
    $selected={selected}
    $size={size}
    onClick={onSelect}
    data-testid={testId}
  >
    <Chip $selected={selected} $size={size} aria-hidden="true">
      {icon}
    </Chip>
    <span>{label}</span>
  </Tile>
);

/** 45: two columns 11.5 apart, rows 13 apart; 56: three columns, 14 both ways. */
export const OptionGrid = styled.div<{ $columns: 2 | 3 }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: ${({ $columns }) => ($columns === 2 ? '13px 11.5px' : '14px')};
`;

// 6985:10400 visibility cards: r12, a title row with its glyph, a two-line hint, a tick when on.
const Card = styled(PlainButton)<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px 16px 18px;
  border-radius: 12px;
  text-align: left;
  border: 1px solid ${({ $selected }) => ($selected ? wz.accent : wz.border)};
  background: ${({ $selected }) => ($selected ? tint(wz.accent, 0.14) : wz.field)};

  &:focus-visible {
    outline: 2px solid ${wz.accent};
    outline-offset: 2px;
  }
`;

const CardTitle = styled.span`
  display: flex;
  align-items: center;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}

  > * + * {
    margin-left: 10px;
  }
`;

const CardHint = styled.span`
  margin-top: 10px;
  color: ${wz.secondary};
  ${wzType.hint}
`;

const Tick = styled.span`
  position: absolute;
  top: 14px;
  right: 16px;
  color: ${wz.accent};
`;

interface ICardChoiceProps {
  icon: React.ReactNode;
  title: string;
  hint: string;
  selected: boolean;
  onSelect: () => void;
}

export const CardChoice: React.FC<ICardChoiceProps> = ({
  icon,
  title,
  hint,
  selected,
  onSelect
}) => (
  <Card type="button" role="radio" aria-checked={selected} $selected={selected} onClick={onSelect}>
    <CardTitle>
      <span aria-hidden="true" style={{ color: selected ? wz.accent : wz.secondary }}>
        {icon}
      </span>
      <span>{title}</span>
    </CardTitle>
    <CardHint>{hint}</CardHint>
    {selected ? (
      <Tick aria-hidden="true">
        <CheckOutlined />
      </Tick>
    ) : null}
  </Card>
);
